import { DeskConfig } from '../types/tradingFloor.types';

export const TILE_SIZE = 48;
export const BASE_TILE = 16;
export const SCALE_FACTOR = 3;
export const OFFICE_COLS = 20;
export const OFFICE_ROWS = 20;  // expanded: rows 14-19 are the lounge area
export const CANVAS_WIDTH = TILE_SIZE * OFFICE_COLS;   // 960
export const CANVAS_HEIGHT = TILE_SIZE * OFFICE_ROWS;  // 672

export const AVATAR_BASE_W = 16;
export const AVATAR_BASE_H = 24;

export const DESK_CONFIGS: DeskConfig[] = [
  // Row 1 (y=3)
  { modelName: 'GASBERT', displayName: 'GASBERT', gridX: 2,  gridY: 3, paletteKey: 'blue' },
  { modelName: 'USOII',  displayName: 'USOII',  gridX: 6,  gridY: 3, paletteKey: 'green' },
  { modelName: 'LOI',    displayName: 'LOI',     gridX: 10, gridY: 3, paletteKey: 'red' },
  { modelName: 'GOI',    displayName: 'GOI',     gridX: 14, gridY: 3, paletteKey: 'gold' },
  // Row 2 (y=7)
  { modelName: 'H1_MFE_ML', displayName: 'H1 MFE', gridX: 2,  gridY: 7, paletteKey: 'cyan' },
  { modelName: 'SILVIO', displayName: 'SILVIO',  gridX: 6,  gridY: 7, paletteKey: 'purple' },
  { modelName: 'RUS',    displayName: 'RUS',     gridX: 10, gridY: 7, paletteKey: 'orange' },
  { modelName: 'QUINI',  displayName: 'QUINI',   gridX: 14, gridY: 7, paletteKey: 'pink' },
  // Row 3 (y=11)
  { modelName: 'LIBRA',  displayName: 'LIBRA',   gridX: 2,  gridY: 11, paletteKey: 'teal' },
  { modelName: 'COOP',   displayName: 'COOP',    gridX: 6,  gridY: 11, paletteKey: 'brown' },
  { modelName: 'YENI',   displayName: 'YENI',    gridX: 10, gridY: 11, paletteKey: 'gray' },
  { modelName: 'DUS',    displayName: 'DUS',     gridX: 14, gridY: 11, paletteKey: 'lime' },
];

// Manager patrol waypoints (grid coordinates)
export const MANAGER_PATROL = [
  { x: 17, y: 3 },
  { x: 17, y: 7 },
  { x: 17, y: 11 },
  { x: 17, y: 7 },
];

export const MANAGER_SPEED = 0.5; // pixels per tick

// Water cooler position (matches OfficeFloor.tsx drawing)
export const WATER_COOLER_X = TILE_SIZE * 18.5 + 12;
export const WATER_COOLER_Y = TILE_SIZE * 6 + 30;

// Printer/copier position (next to water cooler, right side)
export const PRINTER_X = TILE_SIZE * 18.2;
export const PRINTER_Y = TILE_SIZE * 8;

// Coffee stand position (next to upper-right potted plant)
export const COFFEE_X = TILE_SIZE * 18.2;
export const COFFEE_Y = TILE_SIZE * 2.3;

// Avatar walking speed (pixels per tick at 60fps)
export const AVATAR_WALK_SPEED = 1.8;

// Dance floor / party area (bottom-right of lounge)
export const DANCE_FLOOR_X = TILE_SIZE * 14;
export const DANCE_FLOOR_Y = TILE_SIZE * 15;
export const DANCE_FLOOR_W = TILE_SIZE * 5.5;
export const DANCE_FLOOR_H = TILE_SIZE * 4;

// Right-side corridor X — open space with no desks, used for vertical travel
export const CORRIDOR_X = TILE_SIZE * 17;

// Lounge furniture positions
export const ARCADE_X = TILE_SIZE * 7;
export const ARCADE_Y = TILE_SIZE * 14.5;
export const FOOSBALL_X = TILE_SIZE * 10;
export const FOOSBALL_Y = TILE_SIZE * 16;

// Max simultaneous foosball players
export const FOOSBALL_MAX_PLAYERS = 2;

// Solid obstacle bounding boxes — avatars cannot walk through these
export const OBSTACLES: { x: number; y: number; w: number; h: number }[] = [];

// Office boundary — avatars must stay inside
export const OFFICE_BOUNDS = {
  minX: 4,
  minY: TILE_SIZE * 2,  // below the wall
  maxX: OFFICE_COLS * TILE_SIZE - 4,
  maxY: OFFICE_ROWS * TILE_SIZE - 4,
};

// Sleeping area — bottom-left of lounge (where the sofa used to be)
export const SLEEP_AREA_X = TILE_SIZE * 0.5;
export const SLEEP_AREA_Y = TILE_SIZE * 14.5;

// Sleeping spots — all in the sleeping area (bottom-left of lounge)
export const SLEEPING_SPOTS: { x: number; y: number }[] = [
  { x: TILE_SIZE * 1,   y: TILE_SIZE * 15 },
  { x: TILE_SIZE * 3.5, y: TILE_SIZE * 15 },
  { x: TILE_SIZE * 1,   y: TILE_SIZE * 16.5 },
  { x: TILE_SIZE * 3.5, y: TILE_SIZE * 16.5 },
  { x: TILE_SIZE * 1,   y: TILE_SIZE * 18 },
  { x: TILE_SIZE * 3.5, y: TILE_SIZE * 18 },
];

// Aisle Y positions — just below each row's desk legs (gridY*TILE + 28 + 10)
// Tight to desks so the "stand up" transition is very short
export const AISLE_ROWS: Record<number, number> = {
  3: TILE_SIZE * 3 + 38,    // row 1: 144+38 = 182px (desk legs end at 172)
  7: TILE_SIZE * 7 + 38,    // row 2: 336+38 = 374px (desk legs end at 364)
  11: TILE_SIZE * 11 + 38,  // row 3: 528+38 = 566px (desk legs end at 556)
};

// Compute waypoints for walking from desk to a destination, routing through corridors
// to avoid walking through desks.
export function computeWaypoints(
  fromX: number, fromY: number,
  toX: number, toY: number,
  deskGridY: number
): { x: number; y: number }[] {
  const aisleY = AISLE_ROWS[deskGridY] ?? TILE_SIZE * 3 + 38;

  // Check if destination is in the same row zone (within ~2 tiles of our aisle)
  const sameRow = Math.abs(toY - aisleY) < TILE_SIZE * 2.5;

  if (sameRow) {
    // Same row — simple: step into aisle, walk across, arrive
    return [
      { x: fromX, y: aisleY },
      { x: toX, y: aisleY },
      { x: toX, y: toY },
    ];
  }

  // Cross-row travel — route through corridors to avoid desks AND lounge furniture
  // Lounge furniture (arcade ~TILE*7, foosball ~TILE*10) blocks the middle of the lounge.
  // For left-side lounge destinations (x < TILE*6), use the left corridor to avoid furniture.
  // For right-side destinations, use the right corridor.
  const inLounge = toY > TILE_SIZE * 13;
  const useLeftCorridor = toX < TILE_SIZE * 2 || (inLounge && toX < TILE_SIZE * 6);
  const corridorX = useLeftCorridor ? TILE_SIZE * 0.3 : CORRIDOR_X;

  if (inLounge && !useLeftCorridor && toX < TILE_SIZE * 13) {
    // Mid-lounge destination (between furniture) — route via right corridor, drop below desks,
    // then cut across at the destination Y to avoid the arcade/foosball columns
    return [
      { x: fromX, y: aisleY },            // step into own aisle
      { x: CORRIDOR_X, y: aisleY },       // walk to right corridor
      { x: CORRIDOR_X, y: toY },          // walk down in corridor
      { x: toX, y: toY },                 // walk to destination
    ];
  }

  return [
    { x: fromX, y: aisleY },            // step into own aisle
    { x: corridorX, y: aisleY },         // walk to corridor
    { x: corridorX, y: toY },            // walk up/down in corridor (no desks here)
    { x: toX, y: toY },                  // walk to destination
  ];
}

// Get a neighbor desk for chatting — returns aisle Y so avatars chat in the walkway
export function getNeighborDesk(modelName: string): { x: number; aisleY: number; gridY: number } | null {
  const idx = DESK_CONFIGS.findIndex(d => d.modelName === modelName);
  if (idx < 0) return null;
  const desk = DESK_CONFIGS[idx];
  const rightNeighbor = DESK_CONFIGS.find(d => d.gridY === desk.gridY && d.gridX > desk.gridX);
  const leftNeighbor = DESK_CONFIGS.find(d => d.gridY === desk.gridY && d.gridX < desk.gridX);
  const neighbor = rightNeighbor || leftNeighbor;
  if (!neighbor) return null;
  const aisleY = AISLE_ROWS[desk.gridY] ?? TILE_SIZE * 3 + 38;
  return { x: neighbor.gridX * TILE_SIZE, aisleY, gridY: neighbor.gridY };
}

// Model personality info for dialog (instruments from backend tradingsettings.json)
export const MODEL_PERSONALITIES: Record<string, { instrument: string; specialty: string; personality: string }> = {
  GASBERT:    { instrument: 'Natural Gas',   specialty: 'commodities',  personality: 'The energy expert. Loves volatility and big moves.' },
  USOII:     { instrument: 'US100',          specialty: 'tech indices', personality: 'Keeps a cool head tracking the Nasdaq 100 futures.' },
  LOI:       { instrument: 'Oil Crude',      specialty: 'crude oil',    personality: 'A sophisticated analyst with a taste for black gold.' },
  GOI:       { instrument: 'Gold',           specialty: 'precious metals', personality: 'The safe-haven specialist. When in doubt, go gold.' },
  H1_MFE_ML: { instrument: 'Natural Gas',   specialty: 'MFE analysis', personality: 'The quant. Uses Maximum Favorable Excursion for NatGas entries.' },
  SILVIO:    { instrument: 'Silver',         specialty: 'precious metals', personality: 'Gold\'s scrappy little sibling. Fast and unpredictable.' },
  RUS:       { instrument: 'EUR/USD',        specialty: 'forex majors', personality: 'Tracks the world\'s most traded pair. Precision is everything.' },
  QUINI:     { instrument: 'US500',          specialty: 'US indices',   personality: 'Lives and breathes the S&P 500. The market never sleeps.' },
  LIBRA:     { instrument: 'GBP/USD',        specialty: 'forex majors', personality: 'Trades the cable pair. Proper British precision.' },
  COOP:      { instrument: 'Copper',         specialty: 'base metals',  personality: 'Dr. Copper knows the economy. Steady and reliable.' },
  YENI:      { instrument: 'USD/JPY',        specialty: 'forex majors', personality: 'Riding the yen waves. Loves a good carry trade breakout.' },
  DUS:       { instrument: 'AUD/USD',        specialty: 'forex majors', personality: 'Down under currency specialist. Efficient and calculated.' },
};

// Model name resolution: maps backend model strings to our desk model names
export const MODEL_NAME_MAP: Record<string, string> = {
  'GASBERT_DualEvent_Hybrid_v0': 'GASBERT',
  'USOII_GlobalEvent_Classifier_v0': 'USOII',
  'LOI_GlobalEvent_Classifier_v0': 'LOI',
  'GOI_GlobalEvent_Classifier_v0': 'GOI',
  'SILVIO_GlobalEvent_Classifier_v0': 'SILVIO',
  'RUS_GlobalEvent_Classifier_v0': 'RUS',
  'QUINI_GlobalEvent_Classifier_v0': 'QUINI',
  'LIBRA_GlobalEvent_Classifier_v0': 'LIBRA',
  'COOP_GlobalEvent_Classifier_v0': 'COOP',
  'YENI_GlobalEvent_Classifier_v0': 'YENI',
  'DUS_GlobalEvent_Classifier_v0': 'DUS',
  'HOUR_MFE_ML': 'H1_MFE_ML',
  // Direct names (in case the model field is already short)
  'GASBERT': 'GASBERT',
  'USOII': 'USOII',
  'LOI': 'LOI',
  'GOI': 'GOI',
  'SILVIO': 'SILVIO',
  'RUS': 'RUS',
  'QUINI': 'QUINI',
  'LIBRA': 'LIBRA',
  'COOP': 'COOP',
  'YENI': 'YENI',
  'DUS': 'DUS',
  'H1_MFE_ML': 'H1_MFE_ML',
};

export function resolveModelName(rawModel: string): string {
  if (MODEL_NAME_MAP[rawModel]) return MODEL_NAME_MAP[rawModel];

  // Strip "DCA " prefix from DCA trades (backend sends "DCA GASBERT", etc.)
  const stripped = rawModel.startsWith('DCA ') ? rawModel.slice(4) : rawModel;
  if (stripped !== rawModel && MODEL_NAME_MAP[stripped]) return MODEL_NAME_MAP[stripped];

  // Handle "H1 MFE ML" with spaces (backend sends spaces, we use underscores)
  const underscored = stripped.replace(/\s+/g, '_');
  if (MODEL_NAME_MAP[underscored]) return MODEL_NAME_MAP[underscored];

  // Try prefix match
  for (const [key, value] of Object.entries(MODEL_NAME_MAP)) {
    if (stripped.startsWith(key.split('_')[0])) return value;
  }
  return rawModel;
}
